"use client"

import { useCallback } from "react"
import {
  useDisplayCurrency,
  useDisplayRates,
} from "@lib/context/display-currency-context"
import { toDisplayAmount } from "@lib/util/display-currency"
import { convertToLocale } from "@lib/util/money"

/**
 * The ONE component that renders a money amount. Redmine #166532.
 * Spec: docs/display-currency-and-destination.md
 *
 * When the customer has chosen a display currency, the whole storefront shows
 * that currency and nothing else — product pages, listings, cart and the checkout
 * breakdown. Exactly one figure is exempt: the PAYMENT TOTAL, which always shows
 * the region's charge currency and carries the single disclosure line. That is the
 * Amazon pattern: browse and reason in a familiar currency, be told once and
 * unambiguously what the card is actually debited in.
 *
 * Pass `chargeOnly` for that payment total. Everything else converts.
 *
 * With no display currency chosen — the default — this renders the native price,
 * so the storefront is byte-identical to before.
 */

type Props = {
  amount: number | null | undefined
  /** The cart/region currency. What the customer is actually charged in. */
  currencyCode: string
  /** Never convert. Reserved for the payment total. */
  chargeOnly?: boolean
  className?: string
  "data-testid"?: string
}

export default function DisplayPrice({
  amount,
  currencyCode,
  chargeOnly = false,
  className,
  "data-testid": testId,
}: Props) {
  const displayCurrency = useDisplayCurrency()
  const rates = useDisplayRates()

  const converted = chargeOnly
    ? null
    : toDisplayAmount(amount, currencyCode, displayCurrency, rates)

  const shownAmount = converted ?? amount ?? 0
  const shownCurrency = converted !== null ? displayCurrency! : currencyCode

  return (
    <span
      className={className}
      data-testid={testId}
      // The native amount stays in the DOM for tests and analytics even when a
      // converted figure is displayed, so the charge value is never lost.
      data-value={amount ?? 0}
      data-charge-currency={currencyCode}
    >
      {convertToLocale({ amount: shownAmount, currency_code: shownCurrency })}
    </span>
  )
}

/**
 * Formatter form of the same rule, for the places that need a STRING rather than
 * an element — shipping option labels, promotion descriptions, template literals.
 *
 * Shares toDisplayAmount() with the component above so there is exactly one
 * definition of "which currency does this amount render in".
 */
export function useDisplayPrice() {
  const displayCurrency = useDisplayCurrency()
  const rates = useDisplayRates()

  return useCallback(
    (amount: number | null | undefined, chargeCurrency: string) => {
      const converted = toDisplayAmount(amount, chargeCurrency, displayCurrency, rates)
      return converted !== null
        ? convertToLocale({ amount: converted, currency_code: displayCurrency! })
        : convertToLocale({ amount: amount ?? 0, currency_code: chargeCurrency })
    },
    [displayCurrency]
  )
}
